import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CorrelationMatrix = () => {
  const [selectedPair, setSelectedPair] = useState(null);
  const [timeframe, setTimeframe] = useState('1D');

  // Mock correlation data
  const currencyPairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD', 'EURJPY'];
  
  const correlationData = {
    EURUSD: { EURUSD: 1.00, GBPUSD: 0.73, USDJPY: -0.45, AUDUSD: 0.68, USDCAD: -0.52, USDCHF: -0.78, NZDUSD: 0.61, EURJPY: 0.34 },
    GBPUSD: { EURUSD: 0.73, GBPUSD: 1.00, USDJPY: -0.38, AUDUSD: 0.71, USDCAD: -0.48, USDCHF: -0.69, NZDUSD: 0.64, EURJPY: 0.29 },
    USDJPY: { EURUSD: -0.45, GBPUSD: -0.38, USDJPY: 1.00, AUDUSD: -0.41, USDCAD: 0.56, USDCHF: 0.67, NZDUSD: -0.39, EURJPY: 0.78 },
    AUDUSD: { EURUSD: 0.68, GBPUSD: 0.71, USDJPY: -0.41, AUDUSD: 1.00, USDCAD: -0.58, USDCHF: -0.72, NZDUSD: 0.82, EURJPY: 0.25 },
    USDCAD: { EURUSD: -0.52, GBPUSD: -0.48, USDJPY: 0.56, AUDUSD: -0.58, USDCAD: 1.00, USDCHF: 0.61, NZDUSD: -0.54, EURJPY: 0.31 },
    USDCHF: { EURUSD: -0.78, GBPUSD: -0.69, USDJPY: 0.67, AUDUSD: -0.72, USDCAD: 0.61, USDCHF: 1.00, NZDUSD: -0.66, EURJPY: -0.23 },
    NZDUSD: { EURUSD: 0.61, GBPUSD: 0.64, USDJPY: -0.39, AUDUSD: 0.82, USDCAD: -0.54, USDCHF: -0.66, NZDUSD: 1.00, EURJPY: 0.19 },
    EURJPY: { EURUSD: 0.34, GBPUSD: 0.29, USDJPY: 0.78, AUDUSD: 0.25, USDCAD: 0.31, USDCHF: -0.23, NZDUSD: 0.19, EURJPY: 1.00 }
  };

  const getCorrelationColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.8) return 'bg-red-500';
    if (abs >= 0.6) return 'bg-orange-500';
    if (abs >= 0.4) return 'bg-yellow-500';
    if (abs >= 0.2) return 'bg-green-500';
    return 'bg-blue-500';
  };

  const getCorrelationIntensity = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.8) return 'opacity-100';
    if (abs >= 0.6) return 'opacity-80';
    if (abs >= 0.4) return 'opacity-60';
    if (abs >= 0.2) return 'opacity-40';
    return 'opacity-20';
  };

  const getCorrelationText = (correlation) => {
    if (correlation === 1.00) return 'text-white font-bold';
    return Math.abs(correlation) >= 0.5 ? 'text-white font-medium' : 'text-gray-800 font-medium';
  };

  const getRiskLevel = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.8) return 'Very High';
    if (abs >= 0.6) return 'High';
    if (abs >= 0.4) return 'Medium';
    if (abs >= 0.2) return 'Low';
    return 'Very Low';
  };

  const handleCellClick = (pair1, pair2) => {
    if (pair1 !== pair2) {
      setSelectedPair({ pair1, pair2, correlation: correlationData?.[pair1]?.[pair2] });
    }
  };

  const timeframes = ['1H', '4H', '1D', '1W', '1M'];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="GitBranch" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Position Correlation Matrix</h2>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Timeframe Selector */}
          <div className="flex bg-muted rounded-lg p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-200 ${
                  timeframe === tf
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
          
          <button className="flex items-center space-x-1 px-3 py-2 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors duration-200">
            <Icon name="Download" size={16} />
            <span>Export</span>
          </button>
        </div>
      </div>
      {/* Correlation Legend */}
      <div className="flex items-center justify-center space-x-6 mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="text-xs text-muted-foreground">Correlation Strength:</div>
        {[
          { range: '0.8+', color: 'bg-red-500', label: 'Very High' },
          { range: '0.6-0.8', color: 'bg-orange-500', label: 'High' },
          { range: '0.4-0.6', color: 'bg-yellow-500', label: 'Medium' },
          { range: '0.2-0.4', color: 'bg-green-500', label: 'Low' },
          { range: '0-0.2', color: 'bg-blue-500', label: 'Very Low' }
        ]?.map((item) => (
          <div key={item?.range} className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded ${item?.color}`}></div>
            <div className="text-xs">
              <div className="font-medium text-foreground">{item?.label}</div>
              <div className="text-muted-foreground">{item?.range}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Correlation Matrix */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          <table className="w-full">
            <thead>
              <tr>
                <th className="w-20 h-12"></th>
                {currencyPairs?.map((pair) => (
                  <th key={pair} className="w-16 h-12 text-xs font-medium text-muted-foreground">
                    <div className="transform -rotate-45 origin-center">{pair}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currencyPairs?.map((pair1) => (
                <tr key={pair1}>
                  <td className="w-20 h-12 text-xs font-medium text-muted-foreground text-right pr-2">
                    {pair1}
                  </td>
                  {currencyPairs?.map((pair2) => {
                    const correlation = correlationData?.[pair1]?.[pair2];
                    return (
                      <td key={pair2} className="w-16 h-12 p-1">
                        <div
                          className={`w-full h-full flex items-center justify-center rounded cursor-pointer transition-all duration-200 hover:scale-110 ${
                            getCorrelationColor(correlation)
                          } ${getCorrelationIntensity(correlation)} ${
                            selectedPair && 
                            ((selectedPair?.pair1 === pair1 && selectedPair?.pair2 === pair2) ||
                             (selectedPair?.pair1 === pair2 && selectedPair?.pair2 === pair1))
                              ? 'ring-2 ring-primary ring-offset-2' :''
                          }`}
                          onClick={() => handleCellClick(pair1, pair2)}
                          title={`${pair1} vs ${pair2}: ${correlation?.toFixed(2)}`}
                        >
                          <span className={`text-xs ${getCorrelationText(correlation)}`}>
                            {correlation?.toFixed(2)}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Selected Pair Details */}
      {selectedPair && (
        <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h3 className="text-sm font-semibold text-foreground">
                {selectedPair?.pair1} vs {selectedPair?.pair2}
              </h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Correlation:</span>
                <span className={`text-sm font-mono font-bold ${
                  Math.abs(selectedPair?.correlation) >= 0.6 
                    ? 'text-red-600 dark:text-red-400' :'text-green-600 dark:text-green-400'
                }`}>
                  {selectedPair?.correlation?.toFixed(3)}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  Math.abs(selectedPair?.correlation) >= 0.8 
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : Math.abs(selectedPair?.correlation) >= 0.6
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                }`}>
                  {getRiskLevel(selectedPair?.correlation)} Risk
                </span>
              </div>
            </div>
            <button
              onClick={() => setSelectedPair(null)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          
          <div className="mt-3 text-xs text-muted-foreground">
            {Math.abs(selectedPair?.correlation) >= 0.8 && (
              <p className="text-red-600 dark:text-red-400">
                ⚠️ Very high correlation detected. Consider reducing exposure to avoid concentration risk.
              </p>
            )}
            {Math.abs(selectedPair?.correlation) >= 0.6 && Math.abs(selectedPair?.correlation) < 0.8 && (
              <p className="text-orange-600 dark:text-orange-400">
                ⚠️ High correlation detected. Monitor positions closely for risk management.
              </p>
            )}
            {Math.abs(selectedPair?.correlation) < 0.6 && (
              <p className="text-green-600 dark:text-green-400">
                ✓ Acceptable correlation level. Good diversification between these pairs.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrelationMatrix;